import React, { useState, useEffect, useCallback } from 'react';
import AppShell from '../components/AppShell';
import NoteEditor from '../components/NoteEditor';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  StickyNote,
  Plus,
  Search,
  Pin,
  MoreVertical,
  Pencil,
  Trash2,
  Lock,
  Users,
  Globe,
  Tag,
  Link2,
  Calendar,
} from 'lucide-react';
import { cn, formatDate } from '../lib/utils';
import api from '../lib/api';
import { toast } from 'sonner';

export default function NotesPage() {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, pinned
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [deleteConfirmNote, setDeleteConfirmNote] = useState(null);

  // Load notes
  const loadNotes = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filter === 'pinned') params.pinned = true;
      if (searchQuery.trim()) params.search = searchQuery;
      if (selectedTags.length > 0) params.tags = selectedTags.join(',');

      const response = await api.get('/notes', { params });
      setNotes(response.data);
    } catch (error) {
      console.error('Failed to load notes:', error);
      toast.error('Failed to load notes');
    } finally {
      setLoading(false);
    }
  }, [filter, searchQuery, selectedTags]);

  useEffect(() => {
    loadNotes();
  }, [loadNotes]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      loadNotes();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleCreateNote = async (noteData) => {
    try {
      await api.post('/notes', noteData);
      toast.success('Note created');
      setIsCreateDialogOpen(false);
      loadNotes();
    } catch (error) {
      console.error('Failed to create note:', error);
      toast.error('Failed to create note');
    }
  };

  const handleUpdateNote = async (noteData) => {
    try {
      await api.put(`/notes/${editingNote.id}`, noteData);
      toast.success('Note updated');
      setEditingNote(null);
      loadNotes();
    } catch (error) {
      console.error('Failed to update note:', error);
      toast.error('Failed to update note');
    }
  };

  const handleDeleteNote = async () => {
    if (!deleteConfirmNote) return;
    try {
      await api.delete(`/notes/${deleteConfirmNote.id}`);
      toast.success('Note deleted');
      setDeleteConfirmNote(null);
      loadNotes();
    } catch (error) {
      console.error('Failed to delete note:', error);
      toast.error('Failed to delete note');
    }
  };

  const handleTogglePin = async (note) => {
    try {
      await api.put(`/notes/${note.id}`, { pinned: !note.pinned });
      toast.success(note.pinned ? 'Note unpinned' : 'Note pinned');
      loadNotes();
    } catch (error) {
      console.error('Failed to toggle pin:', error);
      toast.error('Failed to update note');
    }
  };

  const getVisibilityIcon = (visibility) => {
    switch (visibility) {
      case 'private':
        return Lock;
      case 'shared':
        return Users;
      case 'org':
        return Globe;
      default:
        return Lock;
    }
  };

  const getVisibilityLabel = (visibility) => {
    switch (visibility) {
      case 'private':
        return 'Private';
      case 'shared':
        return 'Shared';
      case 'org':
        return 'Organization';
      default:
        return 'Private';
    }
  };

  // Get all unique tags from notes for filtering
  const allTags = [...new Set(notes.flatMap(n => n.tags || []))];

  return (
    <AppShell>
      <div className="max-w-5xl mx-auto p-6 space-y-6" data-testid="notes-page">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2">Notes</h1>
            <p className="text-muted-foreground">
              Personal notes and observations linked to your work
            </p>
          </div>
          <Button onClick={() => setIsCreateDialogOpen(true)} data-testid="create-note-btn">
            <Plus className="h-4 w-4 mr-2" />
            New Note
          </Button>
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search notes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="notes-search"
            />
          </div>
          <Tabs value={filter} onValueChange={setFilter}>
            <TabsList>
              <TabsTrigger value="all" data-testid="filter-all">All</TabsTrigger>
              <TabsTrigger value="pinned" data-testid="filter-pinned">
                <Pin className="h-3 w-3 mr-1" />
                Pinned
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* Tag Filters */}
        {allTags.length > 0 && (
          <div className="flex gap-2 flex-wrap">
            {allTags.map(tag => (
              <Badge
                key={tag}
                variant={selectedTags.includes(tag) ? 'default' : 'outline'}
                className="cursor-pointer"
                onClick={() => {
                  if (selectedTags.includes(tag)) {
                    setSelectedTags(selectedTags.filter(t => t !== tag));
                  } else {
                    setSelectedTags([...selectedTags, tag]);
                  }
                }}
              >
                <Tag className="h-3 w-3 mr-1" />
                {tag}
              </Badge>
            ))}
            {selectedTags.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedTags([])}
              >
                Clear filters
              </Button>
            )}
          </div>
        )}

        {/* Notes Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {loading ? (
            // Skeleton loaders
            [...Array(6)].map((_, i) => (
              <Card key={i} className="p-4 animate-pulse">
                <div className="h-4 bg-muted rounded w-3/4 mb-3" />
                <div className="h-3 bg-muted rounded w-full mb-2" />
                <div className="h-3 bg-muted rounded w-2/3" />
              </Card>
            ))
          ) : notes.length === 0 ? (
            <div className="col-span-full text-center py-12 border border-dashed border-border rounded-lg">
              <StickyNote className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground mb-4">
                {searchQuery || selectedTags.length > 0
                  ? 'No notes match your search'
                  : 'No notes yet. Create your first note!'}
              </p>
              <Button onClick={() => setIsCreateDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create Note
              </Button>
            </div>
          ) : (
            notes.map(note => {
              const VisibilityIcon = getVisibilityIcon(note.visibility);
              return (
                <Card
                  key={note.id}
                  className={cn(
                    "p-4 cursor-pointer hover:shadow-md transition-shadow",
                    note.pinned && "ring-2 ring-primary/20",
                    note.color && `border-l-4`
                  )}
                  style={note.color ? { borderLeftColor: note.color } : undefined}
                  onClick={() => setEditingNote(note)}
                  data-testid={`note-card-${note.id}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {note.pinned && (
                        <Pin className="h-4 w-4 text-primary" />
                      )}
                      <VisibilityIcon className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={(e) => {
                          e.stopPropagation();
                          setEditingNote(note);
                        }}>
                          <Pencil className="h-4 w-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={(e) => {
                          e.stopPropagation();
                          handleTogglePin(note);
                        }}>
                          <Pin className="h-4 w-4 mr-2" />
                          {note.pinned ? 'Unpin' : 'Pin'}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteConfirmNote(note);
                          }}
                          className="text-destructive"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                  {note.title && (
                    <h3 className="font-semibold mb-2 line-clamp-1">{note.title}</h3>
                  )}

                  <p className="text-sm text-muted-foreground line-clamp-3 mb-3">
                    {note.content}
                  </p>

                  {/* Tags */}
                  {note.tags && note.tags.length > 0 && (
                    <div className="flex gap-1 flex-wrap mb-3">
                      {note.tags.slice(0, 3).map(tag => (
                        <Badge key={tag} variant="secondary" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                      {note.tags.length > 3 && (
                        <Badge variant="secondary" className="text-xs">
                          +{note.tags.length - 3}
                        </Badge>
                      )}
                    </div>
                  )}

                  {/* Linked Resource */}
                  {note.linked_resource_type && (
                    <div className="flex items-center gap-1 text-xs text-muted-foreground mb-2">
                      <Link2 className="h-3 w-3" />
                      <span className="capitalize">{note.linked_resource_type}</span>
                      {note.linked_resource_title && (
                        <span className="truncate">: {note.linked_resource_title}</span>
                      )}
                    </div>
                  )}

                  {/* Footer */}
                  <div className="flex items-center gap-2 text-xs text-muted-foreground mt-auto pt-2 border-t">
                    <Calendar className="h-3 w-3" />
                    {formatDate(note.created_at)}
                  </div>
                </Card>
              );
            })
          )}
        </div>

        {/* Create Note Dialog */}
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Create Note</DialogTitle>
            </DialogHeader>
            <NoteEditor
              onSave={handleCreateNote}
              onCancel={() => setIsCreateDialogOpen(false)}
            />
          </DialogContent>
        </Dialog>

        {/* Edit Note Dialog */}
        <Dialog open={!!editingNote} onOpenChange={(open) => !open && setEditingNote(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Edit Note</DialogTitle>
            </DialogHeader>
            {editingNote && (
              <NoteEditor
                note={editingNote}
                onSave={handleUpdateNote}
                onCancel={() => setEditingNote(null)}
              />
            )}
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={!!deleteConfirmNote} onOpenChange={(open) => !open && setDeleteConfirmNote(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Note?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete this note. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteNote}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AppShell>
  );
}
